import { Channel, useChatContext, Window, MessageOptionsProps } from 'stream-chat-react';
import { useCallback } from 'react';
import { CustomReactionSelector } from '@features/components/Chat/components/ChatContent/Messages/CustomReactions/CustomReactionSelector';
import { CustomEmptyMessagesState } from '@features/components/Chat/components/ChatContent/Messages/EmptyComponents/CustomEmptyMessagesState';
import { CHAT_LABEL_CHAT_DETAILS, CHAT_LABEL_PINNED_MESSAGES } from '@features/components/Chat/constants';
import { CustomMessageTimestamp } from '@features/components/Chat/components/ChatContent/Messages/CustomMessageTimestamp';
import { RightSideThread } from '@features/components/Chat/components/ChatContent/Messages/RightSideThread/RightSideThread';
import { useStreamChatUserMenu } from '@features/components/Chat/components/hooks/useStreamChatUserMenu';
import { MessageJumper } from '@features/components/Chat/components/ChatContent/Messages/MessageJumper';
import { getSearchMessage } from '../searchMessageMap';
import { CustomChannelHeader } from './CustomChannelHeader';
import { useChatMessagesState } from '../../hooks/useChatMessagesState';
import { CustomMessageOptions } from './CustomMessageOptions';
import { customReactions } from './CustomReactions/constants';
import { CustomMessageSimple } from './CustomMessageSimple';
import { useChannelEvents } from '../../hooks/useChannelEvents';
import { SendButton, AttachmentSelectorInitiationButtonContents } from './MessageInputButtons';
import { ChatMessagesContent } from './ChatMessagesContent';
import { PanelTitle } from './PanelTitle';

type ChatMessagesProps = {
  onBack: () => void;
  isReadOnly?: boolean;
  readOnlyMessage?: string;
  inlineLayout?: boolean;
};

export const ChatMessages = ({ onBack, isReadOnly, readOnlyMessage, inlineLayout }: ChatMessagesProps) => {
  const userOptions = useStreamChatUserMenu();
  const { channel, client } = useChatContext();
  const {
    pinnedMessages,
    isPinnedPanelOpen,
    setIsPinnedPanelOpen,
    loadingPinnedMessages,
    isDetailsPanelOpen,
    isChannelPinned,
    isMuted,
    setIsDetailsPanelOpen,
    toggleMuteChannel,
    togglePinChannel,
    fetchPinnedMessages,
  } = useChatMessagesState();

  useChannelEvents(channel, {
    onRemoved: onBack,
    onMessageUpdated: fetchPinnedMessages,
  });

  const closeAllPanels = useCallback(() => {
    setIsPinnedPanelOpen(false);
    setIsDetailsPanelOpen(false);
  }, [setIsPinnedPanelOpen, setIsDetailsPanelOpen]);

  const handleMentionUser = useCallback(() => {
    setIsDetailsPanelOpen(false);
  }, [setIsDetailsPanelOpen]);

  const MessageOptionsWithReadOnly = useCallback(
    (props: MessageOptionsProps) => (
      <CustomMessageOptions {...props} isReadOnly={isReadOnly} userOptions={userOptions} />
    ),
    [isReadOnly, userOptions],
  );

  const isPanelOpen = isPinnedPanelOpen || isDetailsPanelOpen;

  const renderThread = useCallback(() => {
    if (isPanelOpen) return null;
    return <RightSideThread isReadOnly={isReadOnly} readOnlyMessage={readOnlyMessage} />;
  }, [isPanelOpen, isReadOnly, readOnlyMessage]);

  if (!channel || !client.userID) return null;

  const getHeaderContent = () => {
    if (isPinnedPanelOpen) {
      return (
        <PanelTitle
          title={CHAT_LABEL_PINNED_MESSAGES}
          count={pinnedMessages.length}
          image={channel.data?.image}
          chatName={channel.data?.name}
        />
      );
    }
    if (isDetailsPanelOpen) {
      return <PanelTitle title={CHAT_LABEL_CHAT_DETAILS} image={channel.data?.image} chatName={channel.data?.name} />;
    }
    return null;
  };

  const header = (
    <CustomChannelHeader
      isMuted={isMuted}
      isPinned={isChannelPinned}
      showMenu={!isPanelOpen}
      onBack={isPanelOpen ? closeAllPanels : onBack}
      HeaderContent={getHeaderContent()}
      setIsDetailsPanelOpen={setIsDetailsPanelOpen}
      toggleMuteChannel={toggleMuteChannel}
      togglePinChannel={togglePinChannel}
    />
  );

  const content = (
    <ChatMessagesContent
      channel={channel}
      userId={client.userID}
      pinnedMessages={pinnedMessages}
      isPinnedPanelOpen={isPinnedPanelOpen}
      isDetailsPanelOpen={isDetailsPanelOpen}
      loadingPinnedMessages={loadingPinnedMessages}
      isReadOnly={isReadOnly}
      readOnlyMessage={readOnlyMessage}
      inlineLayout={inlineLayout}
      onOpenPinnedPanel={() => setIsPinnedPanelOpen(true)}
      onClosePanels={closeAllPanels}
      onMentionUser={handleMentionUser}
    />
  );

  return (
    <Channel
      key={`${channel.id}-${getSearchMessage(channel)?.id ?? ''}`}
      Message={CustomMessageSimple}
      reactionOptions={customReactions}
      ReactionSelector={CustomReactionSelector}
      EmptyStateIndicator={CustomEmptyMessagesState}
      MessageOptions={MessageOptionsWithReadOnly}
      AttachmentSelectorInitiationButtonContents={AttachmentSelectorInitiationButtonContents}
      SendButton={SendButton}
      MessageTimestamp={CustomMessageTimestamp}
    >
      <MessageJumper />
      {inlineLayout ? (
        <div className="flex flex-col w-full h-full overflow-visible">
          <div className="flex-1 min-h-0 flex flex-row">
            <div className="flex-1 min-w-0 flex flex-col min-h-0 ">
              {header}
              <div className="flex-1 min-w-0 flex flex-col min-h-0">{content}</div>
            </div>
            {renderThread()}
          </div>
        </div>
      ) : (
        <>
          <Window>
            {header}
            {content}
          </Window>
          {renderThread()}
        </>
      )}
    </Channel>
  );
};
